import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  SlidersHorizontal, 
  MapPin, 
  Sparkles, 
  Video, 
  Clock, 
  Users, 
  Heart,
  Globe,
  Church,
  RotateCcw,
  ChevronRight,
  Crown,
  Search
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import CountryPicker from "./CountryPicker";
import type { LocationPickerValue } from "./CountryPicker";
import StatusCircleSelector, { STATUS_CIRCLES } from "./StatusCircleSelector";

// Religion options
const RELIGION_OPTIONS = [
  { value: "any", label: "Any" },
  { value: "christian", label: "Christian" },
  { value: "muslim", label: "Muslim" },
  { value: "jewish", label: "Jewish" },
  { value: "hindu", label: "Hindu" },
  { value: "other", label: "Other" },
];

// Gender options
const GENDER_OPTIONS = [
  { value: "everyone", label: "Everyone" },
  { value: "men", label: "Men" },
  { value: "women", label: "Women" },
];

export interface DiscoveryFilterValues {
  maxDistance: number;
  showVerifiedOnly: boolean;
  religion: string;
  socialStatus: string;
  ageRange: [number, number];
  country: string;
  state: string;
  city: string;
  gender: string;
  hasVideo: boolean;
  activeRecently: boolean;
}

export const DEFAULT_FILTERS: DiscoveryFilterValues = {
  maxDistance: 50,
  showVerifiedOnly: false,
  religion: "any",
  socialStatus: "any",
  ageRange: [18, 50],
  country: "any",
  state: "any",
  city: "any",
  gender: "everyone",
  hasVideo: false,
  activeRecently: false,
};

interface DiscoveryFiltersProps {
  filters: DiscoveryFilterValues;
  onFiltersChange: (filters: Partial<DiscoveryFilterValues>) => void;
  hasLocation: boolean;
  onRequestLocation: () => void;
  variant?: "icon" | "searchbar";
}

const DiscoveryFilters = ({
  filters,
  onFiltersChange,
  hasLocation,
  onRequestLocation,
  variant = "icon",
}: DiscoveryFiltersProps) => {
  const { profile } = useAuthContext();
  const [isOpen, setIsOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<DiscoveryFilterValues>(filters);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [showStatusSelector, setShowStatusSelector] = useState(false);
  const [countryName, setCountryName] = useState<string>("Any Country");
  const [locationDisplay, setLocationDisplay] = useState<string>("Any Location");
  const [showStatusOnProfile, setShowStatusOnProfile] = useState(
    profile?.status_visibility === "public"
  );

  // Load user's status visibility from profile
  useEffect(() => {
    if (profile) {
      setShowStatusOnProfile(profile.status_visibility === "public");
    }
  }, [profile]);

  // Fetch country name when country filter changes
  useEffect(() => {
    const updateLocationDisplay = async () => {
      if (localFilters.country === "any" && localFilters.state === "any" && localFilters.city === "any") {
        setLocationDisplay("Any Location");
        setCountryName("Any Country");
        return;
      }
      
      const parts: string[] = [];
      
      if (localFilters.country !== "any") {
        const { data: countryData } = await supabase
          .from("countries")
          .select("name, flag")
          .eq("code", localFilters.country)
          .single();
        
        if (countryData) {
          parts.push(`${countryData.flag} ${countryData.name}`);
          setCountryName(`${countryData.flag} ${countryData.name}`);
        }
      }
      
      if (localFilters.state !== "any") {
        const { data: stateData } = await supabase
          .from("states")
          .select("name")
          .eq("id", localFilters.state)
          .single();
        
        if (stateData) {
          parts.push(stateData.name);
        }
      }
      
      if (localFilters.city !== "any") {
        const { data: cityData } = await supabase
          .from("cities")
          .select("name")
          .eq("id", localFilters.city)
          .single();
        
        if (cityData) {
          parts.push(cityData.name);
        }
      }
      
      setLocationDisplay(parts.length > 0 ? parts.join(" › ") : "Any Location");
    };
    updateLocationDisplay();
  }, [localFilters.country, localFilters.state, localFilters.city]);

  // Handle extended location selection
  const handleLocationSelect = (location: LocationPickerValue) => {
    updateLocal({
      country: location.countryCode || "any",
      state: location.stateId || "any",
      city: location.cityId || "any",
    });
  };

  // Check if any filters are active (different from defaults)
  const hasActiveFilters = 
    filters.maxDistance !== DEFAULT_FILTERS.maxDistance ||
    filters.showVerifiedOnly !== DEFAULT_FILTERS.showVerifiedOnly ||
    filters.religion !== DEFAULT_FILTERS.religion ||
    filters.socialStatus !== DEFAULT_FILTERS.socialStatus ||
    filters.ageRange[0] !== DEFAULT_FILTERS.ageRange[0] ||
    filters.ageRange[1] !== DEFAULT_FILTERS.ageRange[1] ||
    filters.country !== DEFAULT_FILTERS.country ||
    filters.state !== DEFAULT_FILTERS.state ||
    filters.city !== DEFAULT_FILTERS.city ||
    filters.gender !== DEFAULT_FILTERS.gender ||
    filters.hasVideo !== DEFAULT_FILTERS.hasVideo ||
    filters.activeRecently !== DEFAULT_FILTERS.activeRecently;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setLocalFilters(filters);
    }
    setIsOpen(open);
  };

  const handleApply = () => {
    onFiltersChange(localFilters);
    setIsOpen(false);
  };

  const handleReset = () => {
    setLocalFilters(DEFAULT_FILTERS);
  };

  const updateLocal = (updates: Partial<DiscoveryFilterValues>) => {
    setLocalFilters((prev) => ({ ...prev, ...updates }));
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        {variant === "searchbar" ? (
          <button 
            className="relative inline-flex items-center gap-2 h-9 px-3 max-w-[180px] md:max-w-[220px] rounded-full bg-muted/50 hover:bg-muted border border-border/50 transition-colors"
            aria-label="Open filters"
          >
            <span className="text-xs text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
              Find matches by location and preferences
            </span>
            <Search className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            {hasActiveFilters && (
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-pop rounded-full border border-background" />
            )}
          </button>
        ) : (
          <button 
            className="relative h-10 w-10 rounded-full bg-muted/50 hover:bg-muted border border-border/50 flex items-center justify-center transition-colors"
            aria-label="Open filters"
          >
            <SlidersHorizontal className="h-5 w-5 text-foreground" />
            {hasActiveFilters && (
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-pop rounded-full border border-background" />
            )}
          </button>
        )}
      </SheetTrigger>
      
      <SheetContent side="bottom" className="rounded-t-3xl h-[85vh]">
        <SheetHeader className="pb-2">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2 text-lg">
              <SlidersHorizontal className="w-5 h-5" />
              Find matches by location and preferences
            </SheetTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset
            </Button>
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(85vh-140px)] pr-4">
          <div className="space-y-6 py-4">
            
            {/* Religion Filter */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2 text-base">
                <Church className="w-4 h-4 text-primary" />
                Religion
              </Label>
              <div className="flex flex-wrap gap-2">
                {RELIGION_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => updateLocal({ religion: option.value })}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      localFilters.religion === option.value
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted hover:bg-muted/80 text-foreground"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Social Status Filter - Premium Card Entry */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2 text-base">
                <Crown className="w-4 h-4 text-primary" />
                Status & Lifestyle
              </Label>
              <Button
                variant="outline"
                className="w-full justify-between h-auto py-3"
                onClick={() => setShowStatusSelector(true)}
              >
                <div className="flex items-center gap-3">
                  {localFilters.socialStatus !== "any" ? (
                    <>
                      <div className={`p-1.5 rounded-lg bg-gradient-to-br ${
                        STATUS_CIRCLES.find(c => c.value === localFilters.socialStatus)?.color || "from-gray-400 to-gray-500"
                      } text-white`}>
                        {STATUS_CIRCLES.find(c => c.value === localFilters.socialStatus)?.icon}
                      </div>
                      <span className="font-medium">
                        {STATUS_CIRCLES.find(c => c.value === localFilters.socialStatus)?.label}
                      </span>
                    </>
                  ) : (
                    <>
                      <div className="p-1.5 rounded-lg bg-muted text-muted-foreground">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <span className="text-muted-foreground">All Circles</span>
                    </>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 opacity-50" />
              </Button>
              <p className="text-xs text-muted-foreground">
                Match with people at a similar life stage
              </p>
            </div>

            <StatusCircleSelector
              isOpen={showStatusSelector}
              onClose={() => setShowStatusSelector(false)}
              selectedCircle={localFilters.socialStatus}
              onSelect={(circle) => updateLocal({ socialStatus: circle })}
              showOnProfile={showStatusOnProfile}
              onToggleVisibility={setShowStatusOnProfile}
            />

            {/* Age Range Filter */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-base">
                  <Heart className="w-4 h-4 text-primary" />
                  Age Range
                </Label>
                <span className="text-sm font-semibold text-primary">
                  {localFilters.ageRange[0]} - {localFilters.ageRange[1]}
                </span>
              </div>
              <div className="space-y-4 px-1">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground w-8">Min</span>
                  <Slider
                    value={[localFilters.ageRange[0]]}
                    onValueChange={([v]) => updateLocal({ ageRange: [v, Math.max(v, localFilters.ageRange[1])] })}
                    min={18}
                    max={70}
                    step={1}
                    className="flex-1"
                  />
                  <span className="text-sm font-medium w-8">{localFilters.ageRange[0]}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground w-8">Max</span>
                  <Slider
                    value={[localFilters.ageRange[1]]}
                    onValueChange={([v]) => updateLocal({ ageRange: [Math.min(v, localFilters.ageRange[0]), v] })}
                    min={18}
                    max={70}
                    step={1}
                    className="flex-1"
                  />
                  <span className="text-sm font-medium w-8">{localFilters.ageRange[1]}</span>
                </div>
              </div>
            </div>

            {/* Country Filter */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2 text-base">
                <Globe className="w-4 h-4 text-primary" />
                Location
              </Label>
              <Button
                variant="outline"
                className="w-full justify-between"
                onClick={() => setShowCountryPicker(true)}
              >
                <span className="truncate">{locationDisplay}</span>
                <ChevronRight className="w-4 h-4 opacity-50" />
              </Button>
            </div>

            <CountryPicker
              isOpen={showCountryPicker}
              onClose={() => setShowCountryPicker(false)}
              selectedCountry={localFilters.country}
              onSelect={(code) => updateLocal({ country: code, state: "any", city: "any" })}
              showStateCity={true}
              selectedState={localFilters.state !== "any" ? localFilters.state : undefined}
              selectedCity={localFilters.city !== "any" ? localFilters.city : undefined}
              onSelectLocation={handleLocationSelect}
            />

            {/* Distance Filter */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-base">
                  <MapPin className="w-4 h-4 text-primary" />
                  Maximum Distance
                </Label>
                <span className="text-sm font-semibold text-primary">
                  {localFilters.maxDistance >= 100 ? "100+" : localFilters.maxDistance} miles
                </span>
              </div>

              {!hasLocation ? (
                <div className="p-4 rounded-xl bg-muted/50 border border-dashed border-border">
                  <p className="text-sm text-muted-foreground mb-3">
                    Enable location to filter by distance
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onRequestLocation}
                    className="w-full"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    Enable Location
                  </Button>
                </div>
              ) : (
                <>
                  <Slider
                    value={[localFilters.maxDistance]}
                    onValueChange={([v]) => updateLocal({ maxDistance: v })}
                    min={5}
                    max={100}
                    step={5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>5 mi</span>
                    <span>25 mi</span>
                    <span>50 mi</span>
                    <span>100+ mi</span>
                  </div>
                </>
              )}
            </div>

            {/* Gender / Show Me Filter */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2 text-base">
                <Users className="w-4 h-4 text-primary" />
                Show Me
              </Label>
              <div className="flex gap-2">
                {GENDER_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => updateLocal({ gender: option.value })}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                      localFilters.gender === option.value
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted hover:bg-muted/80 text-foreground"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Toggle Filters */}
            <div className="space-y-3">
              {/* Has Video Toggle */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-card border">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${localFilters.hasVideo ? "bg-primary/10" : "bg-muted"}`}>
                    <Video className={`w-4 h-4 ${localFilters.hasVideo ? "text-primary" : "text-muted-foreground"}`} />
                  </div>
                  <div>
                    <Label htmlFor="has-video" className="text-base cursor-pointer">
                      Has Video
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Only show profiles with intro videos
                    </p>
                  </div>
                </div>
                <Switch
                  id="has-video"
                  checked={localFilters.hasVideo}
                  onCheckedChange={(v) => updateLocal({ hasVideo: v })}
                />
              </div>

              {/* Verified Only Toggle */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-card border">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${localFilters.showVerifiedOnly ? "bg-primary/10" : "bg-muted"}`}>
                    <Sparkles className={`w-4 h-4 ${localFilters.showVerifiedOnly ? "text-primary" : "text-muted-foreground"}`} />
                  </div>
                  <div>
                    <Label htmlFor="verified-only" className="text-base cursor-pointer">
                      Verified Only
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Only show verified profiles
                    </p>
                  </div>
                </div>
                <Switch
                  id="verified-only"
                  checked={localFilters.showVerifiedOnly}
                  onCheckedChange={(v) => updateLocal({ showVerifiedOnly: v })}
                />
              </div>

              {/* Active Recently Toggle */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-card border">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${localFilters.activeRecently ? "bg-primary/10" : "bg-muted"}`}>
                    <Clock className={`w-4 h-4 ${localFilters.activeRecently ? "text-primary" : "text-muted-foreground"}`} />
                  </div>
                  <div>
                    <Label htmlFor="active-recently" className="text-base cursor-pointer">
                      Active Recently
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Only show recently active users
                    </p>
                  </div>
                </div>
                <Switch
                  id="active-recently"
                  checked={localFilters.activeRecently}
                  onCheckedChange={(v) => updateLocal({ activeRecently: v })}
                />
              </div>
            </div>
          </div>
        </ScrollArea>

        <div className="pt-4 border-t">
          <Button
            onClick={handleApply}
            className="w-full pop-gradient"
          >
            Apply Filters
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DiscoveryFilters;
